import { DrawerSheet, DrawerSheetTitle } from '../drawer-sheet';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { UserAvatar } from '../user-avatar';
import { RepliesList } from './replies-list';
import { ReplyInput } from './reply-input';
import { InteractCloseIcon, InteractCommentIcon } from '@/client/assets';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useTranslation } from 'react-i18next';

type RepliesSidebarProps = {
  className?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const RepliesSidebar = ({ open = false, setOpen, className }: RepliesSidebarProps) => {
  const { t } = useTranslation();
  const repliedParams = useModelStore(state => state.repliedParams);
  const user = useSignedUserInfo(api);
  return (
    <DrawerSheet
      open={open}
      setOpen={setOpen}
      // Sheet renders at a fixed physical side='right' (default, unset here) — see
      // ui/sheet.tsx, where side variants are intentionally left physical/unmirrored.
      className={cn('md:border-l bg-card w-full sm:w-102 space-y-3 md:space-y-6', className)}
    >
      <DrawerSheetTitle className="gap-2 flex items-center bg-card">
        <InteractCommentIcon className="size-5 fill-current" />
        <TextTooltip tooltip={t('replies')} className="flex-1 line-clamp-1">
          {t('replies')} ({repliedParams.total})
        </TextTooltip>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setOpen(false)}
          className="transition-all duration-200 hover:scale-90 focus:scale-80"
        >
          <InteractCloseIcon className="!size-6 fill-foreground/40" />
        </Button>
      </DrawerSheetTitle>
      <div className="flex items-center gap-2">
        <UserAvatar avatar={user.data?.avatar} address={user.data?.uid} className="size-8 rounded-1.5" />
        <TextTooltip tooltip={user.data?.name} className="line-clamp-1 text-start flex-1 text-sm leading-5 font-medium">
          {user.data?.name}
        </TextTooltip>
      </div>
      <ReplyInput postUuid={repliedParams.postUuid} />
      <RepliesList postUuid={repliedParams.postUuid} className="flex-1 mt-3 md:mt-6 w-full" />
    </DrawerSheet>
  );
};
